import { type JanusCtx, type Row, ec, ecId, ecLean, handBack, insertRoll, itemField, refuse, scalar, upper, requiredInt } from '../context.js';
import { isSelfStatusOrigin } from '../../statuses/status-origin.js';
import { type JanusInput } from '../janus.js';
import { STATUS_IDS, statusItemId } from '../../statuses/expiry.js';
import { resolveConnectionCascade } from '../../statuses/gm-boundary.js';
import { applyStatusEffect } from '../statuses/apply-status-effect.js';
import { removeStoredStatus } from '../statuses/status-effect-removal-helpers.js';
import { spendAbilityAction } from '../templates/template-abilities-roll.js';
import { lostShapeshift } from '../../port/k4-dispatch/status_removal_api.js';
import { DISMISS_SUMMON, RECALL_SUMMON, REVERT_SHAPESHIFT, PARALYZED } from '../creature-control-ids.js';

export const CUSTOM_TYPES: Record<string, string> = {
  'C79A906F-BA10-B705-1CB9-6A7C5DFAEBEF': 'custom_full_ability',
  'E350D4F5-B29D-17C0-4AB4-0CAA0398C74A': 'custom_ability',
  '41FF250E-4146-C8C2-1324-DD6911BDB372': 'custom_quick_ability',
  'EEB78637-0F73-8369-D230-8C29512B759E': 'custom_free_ability',
  'BF71B9FC-32EF-29EF-9560-7219EBE600CD': 'custom_reaction_ability',
};

/** Built-in controls act on their caster. Summon controls charge its controller. */
export const SUMMON_CONTROL_IDS: Record<string, string> = {
  '5496F09C-94BB-452E-9DDD-47CC871CFB5E': 'end-concentration',
  '8C7CDFC0-E251-4CD3-ACA8-70F095746621': 'dismiss-summon',
  '73AF8EC0-6717-475A-B272-15B2542C91E0': 'recall-summon',
  '84DA8D8C-7B6D-444E-B76F-8B174B14A6C0': 'revert-shapeshift',
};

/** includes/functions/janus/rolls/registry_dispatch_rolls.php:custom_abilities_roll */
export const END_CONCENTRATION = '5496F09C-94BB-452E-9DDD-47CC871CFB5E';

/** summon_control_rolls.php: the actor remains the summon, including its permission gate. */
export function creatureControlRoll(ctx: JanusCtx, input: JanusInput, caster: Row): void {
  const abilityId = upper(input.abilityId);
  if (abilityId === REVERT_SHAPESHIFT) {
    if (Number(scalar(ctx.read.value(caster, 'shapeshifted'))) !== 1 || !ctx.read.statuses(caster).length)
      refuse('Caster is not currently shapeshifted, cannot revert to true form!');
    if (ctx.read.inCombat(ctx.encounter, caster) && requiredInt(ecLean(caster, 'reaction'), 'revert_shapeshift:reaction-not-held') !== 1)
      refuse('You have already used your reaction.');
    spendAbilityAction(ctx, { items: { item_type: 'reaction_ability' } }, caster);
    lostShapeshift(ctx, caster, 'reverted_shapeshift', false);
    return;
  }
  const controllerId = upper(ecLean(caster, 'controller_encounter_creature_id'));
  const controller = ctx.members.get(controllerId);
  if (!controller || controllerId === ecId(caster)) handBack('summon_control:controller-not-held');
  const ability = ctx.read.definition(abilityId);
  if (!ability) handBack('summon_control:definition-not-held');
  spendAbilityAction(ctx, ability, controller);
  const dismiss = abilityId === DISMISS_SUMMON;
  if (dismiss) {
    applyStatusEffect(ctx, input, ability, PARALYZED, '', caster, caster,
      { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, false);
  } else {
    // The reference walks Paralyzed rows, removing at most one ordinary same-target peer per connection.
    for (const status of ctx.read.statuses(caster).filter(s => statusItemId(s) === PARALYZED))
      removeStoredStatus(ctx, caster, status, 'single');
  }
  ec(caster).encounter_creature_private = dismiss ? 1 : 0;
  insertRoll(ctx, { roll_type_1: dismiss ? 'summon_dismissed' : 'summon_recalled', roll_item_id: abilityId, by: controller, to: caster,
    item_display: { item_type: 'full_ability', item_name: dismiss ? 'Dismiss Summon' : 'Recall Summon' } });
}

/** summon_control_rolls.php:end_concentration_roll (FT3D playtest 2026-10-13: the End Concentration button was refused
 *  `legacy-ability-route-removed`). use_ability_variable_1 names ONE of the caster's own stored Concentrating rows (the info panel's button
 *  passes its status_id); anything else is refused by name — PHP dropped the connection of ANY status id it was handed. The
 *  page removes the held connection through the shared GM cascade and names the origin spell. janus.ts's custom-roll dispatch spends the
 *  reaction (in combat only, spendAbilityAction). */
function endConcentrationRoll(ctx: JanusCtx, input: JanusInput, caster: Row): void {
  const statusId = upper(input.useAbilityVariable1);
  const row = ctx.read.statuses(caster).find((s) => upper(s.status_id) === statusId);
  if (!row || row.tick_added || statusItemId(row) !== STATUS_IDS.concentrating) refuse('That concentration has already ended.');
  resolveConnectionCascade(ctx, row, ecId(caster));
  const originId = isSelfStatusOrigin(upper(scalar(row.status_id)), upper(scalar(row.status_origin_id))) ? '' : upper(scalar(row.status_origin_id));
  const origin = originId ? ctx.read.definition(originId) : null;
  const originName = String(itemField(origin, 'item_name') ?? scalar(row.status_origin_id_item_name) ?? '');
  insertRoll(ctx, { roll_type_1: 'end_concentration', roll_item_id: END_CONCENTRATION, by: caster,
    ...(/^[0-9A-F-]{36}$/.test(originId) ? { log_item_target: { item_id: originId, item_name: originName,
      item_type: String(itemField(origin, 'item_type') ?? scalar(row.status_origin_id_item_type) ?? 'spell') } } : {}) });
}

export function customAbilitiesRoll(ctx: JanusCtx, input: JanusInput, caster: Row): boolean {
  const control = SUMMON_CONTROL_IDS[String(input.abilityId ?? '').toUpperCase()];
  if (control === 'end-concentration') { endConcentrationRoll(ctx, input, caster); return true; }
  if (control) { creatureControlRoll(ctx, input, caster); return true; }
  const type = CUSTOM_TYPES[input.abilityId];
  if (!type) return false;
  insertRoll(ctx, { roll_type_1: type, roll_item_id: input.abilityId, by: caster });
  return true;
}
